import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
// import ReactHtmlParser from 'react-html-parser';
import classNames from 'classNames';
import axios from 'axios';
import {
	updateQuestion,
	ajaxRequest,
	addSectionHeading,
	updateAnswer,
} from '~/api';
import {
	reorder,
	updateSectionHeadingsOrder,
	getSectionHeadings,
} from '~/helpers';
import {
	removeEntity,
	removeSectionHeadingEntity,
	updateWorkspaceNodeTitle,
	moveUp,
	moveDown,
	updateQuestionType,
	updateSectionHeadings,
} from '~/redux/actions/workspaceActions';
import { updateNodeTitle } from '~/redux/actions/dataActions';
import Icon from '~/components/common/icon';

/**
 * Header for nodes which contains the title and the View, Edit, Remove, toggle buttons.
 * This is used for Lessons, Topics, Quizzes and Questions in the course and quiz builders
 */
class NodeHeader extends Component {
	/**
	 * Constructor
	 *
	 * @param {Object} props
	 */
	constructor( props ) {
		super( props );

		// cancel token to abort any requests when unmounting the component.
		this.signal = axios.CancelToken.source();

		/**
		 * Our state which contains:
		 * - showForm: whether to show the edit/new title form
		 * - showPointsForm: whether to show the edit points form on the quiz builder
		 * - value: value for the title text input
		 * - is_loading: whether a request is currently processing
		 * - error: in case we have any errors from the AJAX endpoint
		 * - type: the question type if on the quiz builder
		 * - points: the question points if on the quiz builder
		 * - points_temp: value for the points text input on the quiz builder
		 */
		this.state = {
			showForm: false,
			showPointsForm: false,
			is_loading: false,
			error: null,
			value: '',
			type: this.props.type,
			points: this.props.node.points
				? parseInt( this.props.node.points, 10 )
				: 0,
			points_temp: this.props.node.points
				? parseInt( this.props.node.points, 10 )
				: 0,
		};

		this.handleSelectChange = this.handleSelectChange.bind( this );
		this.handleChangePoints = this.handleChangePoints.bind( this );
		this.handleShowPointsForm = this.handleShowPointsForm.bind( this );
		this.handleSavePoints = this.handleSavePoints.bind( this );
		this.moveEntityUp = this.moveEntityUp.bind( this );
		this.moveEntityDown = this.moveEntityDown.bind( this );
	}

	/**
	 * Cancel all requests when unmounting the component.
	 */
	componentWillUnmount() {
		this.signal.cancel();
	}

	/**
	 * Sets the title for the node
	 */
	componentDidMount() {
		this.setState( {
			value: this.props.node.post_title,
		} );
	}

	/**
	 * Data to be submitted when editing a node title.
	 *
	 * @param {handleSubmit} e
	 */
	async handleSubmit( e ) {
		e.preventDefault();

		const { value } = this.state;
		const {
			workspace,
			node,
			updateWorkspaceNodeTitle,
			updateNodeTitle,
		} = this.props;

		const builder_nonce_element = document.getElementById(
			'learndash_builder_nonce'
		);
		let builder_nonce;
		if ( builder_nonce_element ) {
			builder_nonce = builder_nonce_element.value;
		}

		const builder_data =
			learndash_builder_assets[
				LearnDashData.post_data.builder_post_type
			].post_data;
		builder_data.builder_nonce = builder_nonce;

		if ( value ) {
			// Set the loading state.
			this.setState( {
				is_loading: true,
				error: null,
			} );

			// Editing the title for a lesson, topic, quiz or question
			if ( 'section-heading' !== node.type ) {
				const payload = {
					action: 'learndash_builder_selector_step_title',
					builder_data,
					builder_query_args: {
						new_title: value,
						post_id: node.ID,
						post_type: node.type,
					},
				};

				// Make AJAX request.
				const res = await ajaxRequest( payload, {
					cancelToken: this.signal.token,
				} );
				if ( res.data.status ) {
					// Update in workspace
					updateWorkspaceNodeTitle( {
						ID: node.ID,
						post_title: value,
					} );

					// Update in data
					updateNodeTitle( {
						ID: node.ID,
						post_title: value,
						type: node.type,
					} );

					this.setState( {
						showForm: false,
					} );
				} else {
					this.setState( {
						error: `${ LearnDashData.i18n.error }`,
					} );
				}
			} else {
				// We're editing a section heading title
				const currentSections =
					0 < workspace.present.sections.length
						? workspace.present.sections
						: [];
				const newSections = currentSections.map( ( el ) => {
					// Update the current section heading but leave the others intact
					if ( el.ID === node.ID ) {
						return { ...el, post_title: value };
					}
					return el;
				} );

				// Update the post meta
				addSectionHeading(
					LearnDashData.post_data.builder_post_id,
					newSections
				);

				// Update our workspace
				updateWorkspaceNodeTitle( {
					ID: node.ID,
					post_title: value,
				} );

				this.setState( {
					showForm: false,
				} );
			}

			this.setState( {
				is_loading: false,
			} );
		}
	}

	/**
	 * Listens to changes to input fields.
	 *
	 * @param {handleChange} e
	 */
	handleChange( e ) {
		this.setState( {
			value: e.target.value,
		} );
	}

	/**
	 * Listens to changes to input fields.
	 */
	handleShowPointsForm() {
		this.setState( ( { showPointsForm } ) => ( {
			showPointsForm: ! showPointsForm,
		} ) );
	}

	/**
	 * Listens to changes to question type change.
	 *
	 * @param {handleSelectChange} e
	 */
	async handleSelectChange( e ) {
		const { value: question_type } = e.target;
		const { points } = this.state;
		const { node, updateQuestionType } = this.props;
		let res2 = '';
		const answerPointsActivated =
			'assessment_answer' === question_type ? true : false;

		this.setState( {
			showPointsForm: false,
		} );

		const res = await updateQuestion( node.ID, {
			_answerType: question_type,
			_answerPointsActivated: answerPointsActivated,
		} );

		// If this is an essay, update the points in the answerData as well
		if (
			'essay' === question_type ||
			'free_answer' === question_type ||
			'cloze_answer' === question_type ||
			'assessment_answer' === question_type
		) {
			const newPointsObject = { points };
			const newAnswer = {
				...node.answers[
					LearnDashData.questions_types_map[ question_type ]
				][ 0 ],
				...newPointsObject,
			};
			node.answers[
				LearnDashData.questions_types_map[ question_type ]
			][ 0 ] = newAnswer;
			const newAnswers =
				node.answers[
					LearnDashData.questions_types_map[ question_type ]
				];

			res2 = await updateAnswer( node.ID, newAnswers );
		}

		if ( 200 === res || 200 === res2 ) {
			// Update our workspace
			updateQuestionType( {
				...node,
				question_type,
				points,
			} );
		}
	}

	/**
	 * Updates question points
	 *
	 * @param {handleSavePoints} e
	 */
	async handleSavePoints( e ) {
		e.preventDefault();
		const { points, points_temp } = this.state;
		const { node, updateQuestionType } = this.props;
		let res2 = '';

		const newPoints = isNaN( points_temp ) ? points : points_temp;

		this.setState( {
			showPointsForm: false,
			points: parseInt( newPoints, 10 ),
			points_temp: parseInt( newPoints, 10 ),
		} );

		if ( 0 <= points ) {
			const res = await updateQuestion( node.ID, {
				_points: parseInt( newPoints, 10 ),
			} );

			// If this is an essay, update the points in the answerData as well
			if (
				'essay' === node.question_type ||
				'free_answer' === node.question_type ||
				'cloze_answer' === node.question_type ||
				'assessment_answer' === node.question_type
			) {
				const newPointsObject = { points: parseInt( newPoints, 10 ) };
				const newAnswer = {
					...node.answers[
						LearnDashData.questions_types_map[ node.question_type ]
					][ 0 ],
					...newPointsObject,
				};
				node.answers[
					LearnDashData.questions_types_map[ node.question_type ]
				][ 0 ] = newAnswer;
				const newAnswers =
					node.answers[
						LearnDashData.questions_types_map[ node.question_type ]
					];

				res2 = await updateAnswer( node.ID, newAnswers );
			}

			if ( 200 === res || 200 === res2 ) {
				// Update our workspace
				updateQuestionType( {
					...node,
					points: parseInt( newPoints, 10 ),
				} );
			}
		}
	}

	/**
	 * Listens to changes to question points.
	 *
	 * @param {handleChangePoints} e
	 */
	handleChangePoints( e ) {
		const { value: newPoints } = e.target;

		this.setState( {
			points_temp: newPoints,
		} );
	}

	/**
	 * Moves entity up
	 *
	 * @param {moveEntityUp} e
	 */
	moveEntityUp( e ) {
		const {
			node,
			moveUp,
			workspace,
			index,
			updateSectionHeadings,
		} = this.props;
		e.preventDefault();

		// Update our workspace
		moveUp( node.ID );

		// If we're moving lessons or section headings, we need to update the index saved in section headings post meta
		if ( 'sfwd-lessons' === node.type || 'section-heading' === node.type ) {
			if ( 0 < workspace.present.sections.length ) {
				const lessons = reorder(
					workspace.present.lessons,
					index,
					parseInt( index - 1, 10 )
				);
				const newLessons = updateSectionHeadingsOrder( lessons );
				const newSections = getSectionHeadings( newLessons );

				// Update section headings
				updateSectionHeadings( newSections );

				// Update post meta
				addSectionHeading(
					LearnDashData.post_data.builder_post_id,
					newSections
				);
			}
		}
	}

	/**
	 * Moves entity down
	 *
	 * @param {moveEntityDown} e
	 */
	moveEntityDown( e ) {
		const {
			node,
			moveDown,
			workspace,
			index,
			updateSectionHeadings,
		} = this.props;
		e.preventDefault();

		// Update our workspace
		moveDown( node.ID );

		// If we're moving lessons or section headings, we need to update the index saved in section headings post meta
		if ( 'sfwd-lessons' === node.type || 'section-heading' === node.type ) {
			if ( 0 < workspace.present.sections.length ) {
				const lessons = reorder(
					workspace.present.lessons,
					index,
					parseInt( index + 1, 10 )
				);
				const newLessons = updateSectionHeadingsOrder( lessons );
				const newSections = getSectionHeadings( newLessons );

				// Update section headings
				updateSectionHeadings( newSections );

				// Update post meta
				addSectionHeading(
					LearnDashData.post_data.builder_post_id,
					newSections
				);
			}
		}
	}

	/**
	 * Check if the toggle should be shown
	 *
	 * @param node
	 */
	maybeShowToggle( node ) {
		if (
			node &&
			node.type &&
			'sfwd-quiz' !== node.type &&
			'section-heading' !== node.type
		) {
			if (
				'sfwd-question' !== node.type ||
				( 'sfwd-question' === node.type && node.edit_link )
			) {
				return true;
			}
			return false;
		}
		return false;
	}

	/**
	 * Render function
	 *
	 * @event handleSubmit
	 * @event handleChange
	 * @event handleSelectChange
	 * @event handleSavePoints
	 * @event handleChangePoints
	 * @event moveEntityUp
	 * @event moveEntityDown
	 */
	render() {
		const {
			showForm,
			showPointsForm,
			value,
			is_loading,
			error,
			points,
			points_temp,
		} = this.state;
		const {
			node,
			className,
			data,
			provided,
			expanded,
			removeEntity,
			removeSectionHeadingEntity,
			toggleNode,
			type,
			updateSectionHeadings,
		} = this.props;

		// LEARNDASH-3940
		const answerTypeMap = LearnDashData.questions_types_map;
		// Get answer data and retrieve contents of first answer.
		const answerData =
			node.answers && node.answers[ answerTypeMap[ node.question_type ] ];
		const firstAnswer =
			answerData && answerData[ 0 ] && answerData[ 0 ].answer;

		return (
			<div className="ld-node-header">
				<div className="ld-node-header__order">
					{ /* Allow moving only on Lessons, questions and section headings */ }
					{ ( 'sfwd-lessons' === node.type ||
						'sfwd-question' === node.type ||
						'section-heading' === node.type ) && (
						<button
							onClick={ this.moveEntityUp }
							aria-label={ `${ LearnDashData.i18n.move_up }` }
						>
							<Icon icon="chevron-up" />
						</button>
					) }

					{ /* Required for drag and drop */ }
					<span className="ld-node-header__drag-handle">
						<span { ...provided.dragHandleProps }>
							<Icon icon="drag" />
						</span>
					</span>

					{ /* Allow moving only on Lessons, questions and section headings */ }
					{ ( 'sfwd-lessons' === node.type ||
						'sfwd-question' === node.type ||
						'section-heading' === node.type ) && (
						<button
							onClick={ this.moveEntityDown }
							aria-label={ `${ LearnDashData.i18n.move_down }` }
						>
							<Icon icon="chevron-down" />
						</button>
					) }
				</div>

				{ /* Display the first letter of the entity, except for section headings */ }
				{ 'section-heading' !== node.type && (
					<span className={ `${ className } ${ node.type }` }>
						{ data.labels[ node.type ].charAt( 0 ) }
					</span>
				) }

				{ /* Display entity title and make it editable by clicking on it */ }
				<h3>
					{ ! showForm &&
						! node.edit_link &&
						'section-heading' !== node.type && (
							<span>
								<span
									dangerouslySetInnerHTML={ {
										__html: node.post_title,
									} }
								/>
							</span>
						) }
					{ ! showForm &&
						( ( ! node.edit_link &&
							'section-heading' === node.type ) ||
							node.edit_link ) && (
							<span>
								<button
									className="ld-button-reset"
									onClick={ ( e ) => {
										e.preventDefault();
										this.setState( {
											showForm: true,
										} );
									} }
								>
									<span
										dangerouslySetInnerHTML={ {
											__html: node.post_title,
										} }
									/>
									{ node.tree && 0 < node.tree.length ? (
										<span className="ld-node-header__counter">{ ` (${ node.tree.length })` }</span>
									) : (
										''
									) }
									<span className="row-actions">
										<span className="edit">
											<Icon icon="pencil" />
											<span className="screen-reader-text">{ `${ LearnDashData.i18n.rename }` }</span>
										</span>
									</span>
								</button>
							</span>
						) }

					{ /* Entity renaming form */ }
					{ showForm && (
						<div>
							<form
								onSubmit={ ( e ) => {
									this.handleSubmit( e );
								} }
							>
								<input
									type="text"
									autoFocus
									value={ value }
									placeholder={ `${ LearnDashData.i18n.new_element_labels[ type ] }` }
									onChange={ ( e ) => {
										this.handleChange( e );
									} }
								/>

								<input
									type="submit"
									value={ `${ LearnDashData.i18n.save }` }
									className="is-button is-primary components-button ld__builder--new-entity-button"
								/>
								<input
									type="button"
									onClick={ ( e ) => {
										e.preventDefault();
										this.setState( {
											showForm: false,
										} );
									} }
									value={ `${ LearnDashData.i18n.cancel }` }
									className="is-button components-button is-default ld__builder--new-entity-button"
								/>

								{ is_loading && (
									<span className="ld__builder--form-notice">{ `${ LearnDashData.i18n.please_wait }` }</span>
								) }
								{ error && (
									<span className="ld__builder--form-error">
										{ error }
									</span>
								) }
							</form>
						</div>
					) }

					{ 'sfwd-question' === node.type &&
						( '' === node.post_content ||
							( 'essay' !== node.question_type &&
								'' === firstAnswer ) ) && (
							<button
								className="ld-button-reset warning-expand"
								onClick={ toggleNode }
							>
								<div className="warning-icon -header">
									<Icon icon="warning" />
								</div>
								{ '' === node.post_content && (
									<span className="screen-reader-text">{ `${ LearnDashData.i18n.question_empty }` }</span>
								) }
								{ '' === firstAnswer && (
									<span className="screen-reader-text">{ `${ LearnDashData.i18n.answer_missing }` }</span>
								) }
							</button>
						) }
				</h3>

				{ /* Entity actions: edit, remove */ }
				{ ! showForm && (
					<div className="row-actions -right">
						{ 'section-heading' !== node.type && node.edit_link && (
							<span className="edit">
								<a
									href={ node.edit_link.replace(
										/&amp;/g,
										'&'
									) }
								>{ `${ LearnDashData.i18n.edit }` }</a>{ ' ' }
								|{ ' ' }
							</span>
						) }
						<span className="trash">
							<a
								href="#"
								className="submitdelete"
								onClick={ ( e ) => {
									e.preventDefault();

									if ( 'section-heading' === node.type ) {
										const currentSections =
											0 <
											this.props.workspace.present
												.sections.length
												? this.props.workspace.present
														.sections
												: [];
										const newSections = currentSections.filter(
											( el ) => el.ID != node.ID
										);

										// Update workspace section headings
										updateSectionHeadings( newSections );

										addSectionHeading(
											LearnDashData.post_data
												.builder_post_id,
											newSections
										);
										removeSectionHeadingEntity( node.ID );
									} else {
										removeEntity( node.ID );
									}
								} }
							>{ `${ LearnDashData.i18n.remove }` }</a>
						</span>
					</div>
				) }

				{ /* For quiz builder only: display the question points */ }
				{ 'sfwd-quiz' === LearnDashData.post_data.builder_post_type &&
					0 <= points &&
					! expanded && (
						<div>
							<span
								dangerouslySetInnerHTML={ {
									__html:
										LearnDashData.labels.questions_types[
											node.question_type
										],
								} }
							/>
							: { points }
						</div>
					) }

				{ /* For quiz builder only: display the question type select and points edit form */ }
				{ 'sfwd-quiz' === LearnDashData.post_data.builder_post_type &&
					expanded && (
						<div className="ld-node-header__quiz-actions">
							<select
								className="select-question-type"
								name="question_type"
								onChange={ this.handleSelectChange }
								defaultValue={ node.question_type }
							>
								{ Object.entries(
									LearnDashData.labels.questions_types
								).map( ( question_type, index ) => (
									<option
										key={ `type-${ index }` }
										value={ question_type[ 0 ] }
										dangerouslySetInnerHTML={ {
											__html: question_type[ 1 ],
										} }
									/>
								) ) }
							</select>
							{ LearnDashData.labels.points && '' !== points && (
								<span>
									{ ! showPointsForm &&
										'assessment_answer' !==
											node.question_type && (
											<button
												onClick={
													this.handleShowPointsForm
												}
												className="ld-node-header__points-form-toggle ld-button-reset"
											>
												{ `${ points } ` }
												{ 1 === parseInt( points, 10 )
													? LearnDashData.labels
															.points.singular
													: LearnDashData.labels
															.points.plural }
												<Icon icon="pencil" />
											</button>
										) }
									{ ! showPointsForm &&
										'assessment_answer' ===
											node.question_type && (
											<span>
												{ `${ points } ` }
												{ 1 === parseInt( points, 10 )
													? LearnDashData.labels
															.points.singular
													: LearnDashData.labels
															.points.plural }
											</span>
										) }
									{ showPointsForm && (
										<span>
											<form
												onSubmit={
													this.handleSavePoints
												}
												className="ld-node-header__points-form"
											>
												<input
													type="number"
													min="0"
													name="points"
													value={ points_temp }
													onChange={
														this.handleChangePoints
													}
													className="ld-question-points"
												/>
												{ 1 === parseInt( points, 10 )
													? LearnDashData.labels
															.points.singular
													: LearnDashData.labels
															.points.plural }
												<input
													type="submit"
													className="is-button is-primary"
													value={ `${ LearnDashData.i18n.save }` }
												/>
												<input
													type="button"
													onClick={ ( e ) => {
														e.preventDefault();

														this.setState( {
															showPointsForm: false,
															points_temp: points,
														} );
													} }
													value={ `${ LearnDashData.i18n.cancel }` }
													className="is-button components-button is-default"
												/>
											</form>
										</span>
									) }
								</span>
							) }
						</div>
					) }

				{ /* Displays expand/collapse button for Lessons, Topics, Questions */ }
				{ this.maybeShowToggle( node ) && (
					<button
						className={ classNames( {
							'ld-button-reset': true,
							toggle: true,
							'-expanded': expanded,
						} ) }
						onClick={ toggleNode }
					>
						<Icon icon="dropdown" stroke="#0073aa" />
					</button>
				) }
			</div>
		);
	}
}

/**
 * Valid props
 */
NodeHeader.propTypes = {
	workspace: PropTypes.shape( {
		present: PropTypes.object,
	} ),
	provided: PropTypes.object,
	toggleNode: PropTypes.func,
	node: PropTypes.shape( {
		ID: PropTypes.number,
		post_title: PropTypes.string,
		tree: PropTypes.array,
		type: PropTypes.string,
		points: PropTypes.oneOfType( [ PropTypes.string, PropTypes.number ] ),
	} ),
	className: PropTypes.string,
	type: PropTypes.string,
	expanded: PropTypes.bool,
	data: PropTypes.object,
	index: PropTypes.number,
	moveUp: PropTypes.func,
	moveDown: PropTypes.func,
	removeEntity: PropTypes.func,
	removeSectionHeadingEntity: PropTypes.func,
	updateWorkspaceNodeTitle: PropTypes.func,
	updateNodeTitle: PropTypes.func,
	updateQuestionType: PropTypes.func,
	updateSectionHeadings: PropTypes.func,
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = ( state ) => ( {
	...state,
} );

/**
 *
 * @param {*} dispatch
 */
const mapDispatchToProps = ( dispatch ) => ( {
	moveUp: ( id ) => dispatch( moveUp( { ID: id } ) ),
	moveDown: ( id ) => dispatch( moveDown( { ID: id } ) ),
	removeEntity: ( id ) => dispatch( removeEntity( { ID: id } ) ),
	removeSectionHeadingEntity: ( id ) =>
		dispatch( removeSectionHeadingEntity( { ID: id } ) ),
	updateWorkspaceNodeTitle: ( node ) =>
		dispatch( updateWorkspaceNodeTitle( node ) ),
	updateNodeTitle: ( node ) => dispatch( updateNodeTitle( node ) ),
	updateQuestionType: ( node ) => dispatch( updateQuestionType( node ) ),
	updateSectionHeadings: ( node ) =>
		dispatch( updateSectionHeadings( node ) ),
} );

export default connect( mapStateToProps, mapDispatchToProps )( NodeHeader );
